import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import BackgroundImg from "../../assets/images/ProfileDropdownBG.png";
import CrossIcon from "../../assets/icons/CrossIcon";
import { useTheme } from "../../middleware/ThemeContext";
import { ActionType, Column, TableConfig } from "../../types/types";
import { DropResult } from "@hello-pangea/dnd";
import { useGetRolesContactsQuery, useRemoveRoleMutation } from "../../api/contactRoles";
import { skipToken } from "@reduxjs/toolkit/query";
import DataTable from "../../components/listing/DataTable";
import { font } from "../../utils/themes";
import { SkeletonCell } from "../../components/SkeletonCell";
import { PaginationWrapper } from "../../components/listing/AMSListing";
import Pagination from "../../components/listing/Pagination";
import { ActionWrapper } from "../searchQuery/QueryCreation";
import SearchIcon from "../../assets/icons/SearchIcon";
import { StyledInput } from "../../components/listing/ExpandableSearch";
import { useToast } from "../../components/toast/ToastContext";

interface contact {
  companyId: number;
  email: string;
  firstName: string;
  lastName: string;
  id: number;
  signedUrl: string;
}

interface GroupedContactResult {
  contact: contact;
  organizationNames: string;
  contactOrgRoleId: number;
  orgSignedUrl: string;
}

// new data types

interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  image: string | null;
  signedUrl: string;
}

interface Organization {
  id: number;
  name: string;
  signedUrl: string;
}

interface ContactAssignment {
  assignmentId: number;
  contactOrgId: number;
  contact: Contact;
  organization: Organization;
}

interface ContactsApiResponse {
  roleId: number;
  roleName: string;
  totalCount: number;
  page: number;
  perPage: number;
  contacts: ContactAssignment[];
}

//new data types end

interface AssignedContactsPopupProps {
  onClose: () => void;
  roleID: number | undefined;
  onRoleDataChanged?: () => void;
}


const AssignedContactsPopup: React.FC<AssignedContactsPopupProps> = ({
  onClose,
  roleID,
  onRoleDataChanged,
}) => {
  const { theme } = useTheme();
  const showToast = useToast();
  const [loadingID, setLoadingId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');

  const columns = useMemo<Column[]>(
    () => [
      { id: "primaryUser", label: "Name", isSortable: false },
      { id: "organizationName", label: "Organization Name", isSortable: false },
      { id: "email", label: "Email", isSortable: false },
    ],
    [],
  );

  const STORAGE_KEY = `tableConfig_ignore`;
  const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          visibleColumns: columns
            .filter((col) => col.id !== "id")
            .map((col) => col.id),
          page: 1,
          perPage: 10,
          sort: undefined,
          filters: {},
          query: "",
          columnOrder: columns.map((col) => col.id),
        };
  });

  const queryArgs = roleID
    ? {
        id: roleID,
        page: tableConfig.page,
        perPage: tableConfig.perPage,
        query: debouncedSearchValue || undefined,
      }
    : skipToken;

  const { data: allContacts, refetch, isLoading, isFetching } = useGetRolesContactsQuery(
    queryArgs,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
    }, 600);

    return () => window.clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setTableConfig((prev) => ({ ...prev, page: 1 }));
  }, [searchValue, roleID]);


  const [removeRole] = useRemoveRoleMutation();

  const contactsData = (allContacts?.contacts || []) as ContactAssignment[];
  const allTableData = contactsData.map((item) => ({
    id: item?.assignmentId,
    contact_id: item?.contact?.id,
    contactId: item?.contact?.id,
    contactFirstName: item?.contact?.firstName,
    contactLastName: item?.contact?.lastName,
    contactSignedUrl: item?.contact?.signedUrl,
    email: item?.contact?.email,
    organizationName: item.organization?.name,
    contactOrgRoleId: item?.assignmentId,
    orgSignedUrl: item?.organization?.signedUrl,
  }));

  const totalCount = allContacts?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / tableConfig.perPage));
  const tableData = allTableData;
  const roleName = allContacts?.roleName ?? "";

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const newColumnOrder = Array.from(tableConfig.columnOrder);
      const [removed] = newColumnOrder.splice(result.source.index, 1);
      newColumnOrder.splice(result.destination.index, 0, removed);
      setTableConfig((prev) => ({ ...prev, columnOrder: newColumnOrder }));
    },
    [tableConfig.columnOrder],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
  };

  const handelSubmit = async (id: number) => {
    if (!id) return;

    try {
      setLoadingId(id);

      await removeRole(id).unwrap();

      showToast(
        "Success",
        "Permissions removed successfully.",
        "success"
      );

      await refetch();
      
      // Notify parent component to refetch the listing
      if (onRoleDataChanged) {
        onRoleDataChanged();
      }
    } catch (error: any) {
      console.error("removeRole error:", error);

      // Extract backend message safely
      const message =
        error?.data?.message ||
        error?.error ||
        error?.message ||
        "Failed to remove permissions. Please try again.";

      showToast("Failed", message, "error");
    } finally {
      setLoadingId(null);
    }
  };

  const handleAction = useCallback(
    async (action: ActionType, id: string) => {
      const numericId = Number(id);

      if (action === "revoke") {
        // setLoadingId(numericId);
        handelSubmit(numericId);
        return;
      }
    },
    [handelSubmit],
  );

  return (
    <Overlay>
      <Container theme={theme}>
        <CloseIcon onClick={onClose}>
          <CrossIcon />
        </CloseIcon>
        <Body>
          {isLoading || isFetching ? (
            <div className="d-flex align-items-center flex-column mb-3">
              <SkeletonCell style={{ width: "300px", height: "16px" }} className="mb-2" />
              <SkeletonCell style={{ width: "800px", height: "30px" }} className="mb-3" />
              <SkeletonCell style={{ width: "100%", height: "30px" }} />
            </div>
          ) : (
            <>
              <Title theme={theme} className="text-center">
                {roleName} -{" "}
                <span style={{ fontFamily: font.lato }}>
                  {totalCount}
                </span>{" "}
                Contacts
              </Title>
              <Subtitle theme={theme} className="text-center mb-4">
                Manage and track all contacts assigned to your workspace,
                including
                <br /> billing responsibilities, status updates, and
                communication details.
              </Subtitle>
              <ActionWrapper theme={theme} className="mb-3">
                <SearchIcon color={theme.colors.topBarInputText} size={14} />
                <StyledInput
                  type="text"
                  placeholder="Search assigned contacts here"
                  value={searchValue}
                  onChange={handleSearch}
                  theme={theme}
                />
              </ActionWrapper>
            </>
          )}
          <div style={{ borderRadius: '5px', border: `1px solid ${theme.CorporateTheme.colors.border}` }}>
            <DataTable
              routeKey="contacts"
              title="assignedContacts"
              // profiledName
              hideActionColumn={false}
              columns={columns}
              data={tableData}
              columnOrder={tableConfig.columnOrder}
              visibleColumns={tableConfig.visibleColumns}
              sort={tableConfig.sort}
              noDataTitle="No Contact Found"
              noDataSubtitle="No contact found against this role."
              onDragEnd={handleDragEnd}
              skeletonHeight={'29px'}
              loading={isLoading || isFetching}
              perPage={tableConfig.perPage}
              onAction={handleAction}
              minWidth="1000px"
              loadingID={loadingID}
              containerHeight={300}
            />
            
          </div>

          {!isLoading && !isFetching && (
            <PaginationWrapper theme={theme}>
              {totalCount !== 0 ? (
                <Pagination
                  currentPage={tableConfig.page}
                  totalItems={totalCount}
                  perPage={tableConfig.perPage}
                  totalPages={totalPages}
                  onPageChange={(page) => {
                    setTableConfig((prev) => ({ ...prev, page }));
                  }}
                  onLimitChange={(newLimit) =>
                    setTableConfig((prev) => ({
                      ...prev,
                      perPage: newLimit,
                      page: 1,
                    }))
                  }
                />
              ) : null}
            </PaginationWrapper>
          )}
        </Body>
      </Container>
    </Overlay>
  );
};

export default AssignedContactsPopup;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Container = styled("div")(({ theme }) => ({
  width: "100%",
  maxWidth: "1098px",
  background: theme.CorporateTheme.colors.background,
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
  fontFamily: '"Segoe UI", sans-serif',
  padding: "40px",
  position: "relative",
  backgroundImage: `url(${BackgroundImg})`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "top",
}));

const CloseIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
`;

const Body = styled.div`
  padding-bottom: 24px;
  // padding-top: 0px;
`;

const Title = styled("h2")(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  fontFamily: '"Raleway", sans-serif',
  color: theme.CreateFormTheme.colors.primary,
  marginBottom: "12px",
}));

const Subtitle = styled("p")(({ theme }) => ({
  fontSize: "14px",
  color: theme.CorporateTheme.colors.textSecondary,
  fontWeight: 500,
  lineHeight: 1.5,
  fontFamily: '"Raleway", sans-serif',
}));
